// modules/chariot/ui/src/components/FeaturebaseWidget.tsx
import { useEffect } from 'react';

import { useTheme } from '@/app/ThemeContext';
import { useAxios } from '@/hooks/useAxios';
import { useAuth } from '@/state/auth';

// Decode JWT payload to extract expiration time (without validation - server validates)
const getJwtExpiration = (token: string): number | null => {
  try {
    const payload = token.split('.')[1];
    const decoded = JSON.parse(atob(payload));
    return decoded.exp || null; // exp is in seconds since epoch
  } catch {
    return null;
  }
};

// Extend window type for Featurebase
interface FeaturebaseFunction {
  (action: string, config?: Record<string, unknown>): void;
  q?: Array<[string, Record<string, unknown> | undefined]>;
}

declare global {
  interface Window {
    Featurebase?: FeaturebaseFunction;
  }
}

// Global flag to prevent multiple injections across component remounts
let isFeaturebaseInjected = false;

// Global refresh timer ID to persist across component remounts
let refreshTimerId: number | null = null;

// Fetch JWT token from broker
const fetchFeaturebaseJwt = async (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  axios: { post: (url: string, data: unknown) => Promise<any> }
): Promise<string | undefined> => {
  try {
    const response = await axios.post('/broker', {
      credentialId: 'featurebase',
      type: 'featurebase',
      category: 'internal',
      operation: 'get',
      format: ['token'],
      resolution: 'global',
    });

    const token = response.data?.credentialValue?.featurebase;

    if (!token) {
      console.warn('[Featurebase] JWT not found in broker response');
    }

    return token;
  } catch (error) {
    console.warn('[Featurebase] Failed to fetch JWT from broker:', error);
    return undefined;
  }
};

// Schedule token refresh before expiration
const scheduleTokenRefresh = (
  token: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  axios: { post: (url: string, data: unknown) => Promise<any> }
) => {
  const exp = getJwtExpiration(token);
  if (!exp) {
    console.warn(
      '[Featurebase] Could not parse JWT expiration, skipping refresh scheduling'
    );
    return;
  }

  const nowInSeconds = Math.floor(Date.now() / 1000);
  const timeUntilExpiry = exp - nowInSeconds;

  const fiveMinutes = 5 * 60;

  // If token is already expired or about to expire, refresh immediately
  if (timeUntilExpiry <= fiveMinutes) {
    console.warn(
      '[Featurebase] Token expired or expiring soon, refreshing now'
    );
    void refreshFeaturebaseToken(axios);
    return;
  }

  const fiveMinBeforeExpiry = timeUntilExpiry - fiveMinutes;

  // Clear any existing timer
  if (refreshTimerId) {
    clearTimeout(refreshTimerId);
  }

  refreshTimerId = window.setTimeout(() => {
    void refreshFeaturebaseToken(axios);
  }, fiveMinBeforeExpiry * 1000);
};

// Refresh the JWT token and re-identify with Featurebase
const refreshFeaturebaseToken = async (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  axios: { post: (url: string, data: unknown) => Promise<any> }
) => {
  if (!window.Featurebase) {
    console.warn('[Featurebase] SDK not loaded, skipping token refresh');
    return;
  }

  const newToken = await fetchFeaturebaseJwt(axios);

  if (!newToken) {
    return;
  }

  // Re-identify with Featurebase using the new token
  window.Featurebase('identify', {
    organization: 'praetorian',
    featurebaseJwt: newToken,
  });

  // Schedule next refresh
  scheduleTokenRefresh(newToken, axios);
};

// Test utility to reset the injection flag (only for testing)
// eslint-disable-next-line react-refresh/only-export-components
export const __resetFeaturebaseForTesting = () => {
  isFeaturebaseInjected = false;
  if (refreshTimerId) {
    clearTimeout(refreshTimerId);
    refreshTimerId = null;
  }
  const script = document.getElementById('featurebase-sdk');
  if (script) {
    document.body.removeChild(script);
  }
  delete window.Featurebase;
};

export const FeaturebaseWidget = () => {
  const { isSignedIn, whoami } = useAuth();
  const axios = useAxios();
  const { currentTheme } = useTheme();

  useEffect(() => {
    // Don't inject if not signed in
    if (!isSignedIn) {
      return;
    }

    // CRITICAL: Check both global flag AND DOM to prevent duplicate injection
    if (isFeaturebaseInjected || document.getElementById('featurebase-sdk')) {
      return;
    }

    const appId = import.meta.env.VITE_FEATUREBASE_APP_ID;
    if (!appId) {
      console.warn('[Featurebase] VITE_FEATUREBASE_APP_ID not configured');
      return;
    }

    const userEmail = whoami?.tenant?.name || whoami?.principal.name || '';
    const userId = whoami?.principal.sub || userEmail;
    const displayName = userEmail?.split('@')[0] ?? '';

    isFeaturebaseInjected = true;

    // Defer SDK injection to push it out of React's synchronous passive effect commit cycle
    // The Featurebase SDK creates iframes and attaches non-cloneable objects to the DOM,
    // which causes React dev mode's Performance.measure() to throw DataCloneError
    const deferredTimerId = window.setTimeout(() => {
      // Initialize Featurebase SDK loader function
      const featurebaseFunction: FeaturebaseFunction = function (
        action,
        config
      ) {
        if (!featurebaseFunction.q) {
          featurebaseFunction.q = [];
        }
        featurebaseFunction.q.push([action, config]);
      };
      window.Featurebase = featurebaseFunction;

      // Inject Featurebase widget script
      const script = document.createElement('script');
      script.id = 'featurebase-sdk';
      script.src = 'https://do.featurebase.app/js/sdk.js';
      script.async = true;
      // SRI intentionally omitted: Featurebase manages sdk.js on their CDN and
      // updates it without notice. A static SRI hash would break the widget on
      // every vendor update (hash mismatch → silent load failure). Risk accepted
      // given: HTTPS transport, Statsig feature gate kill-switch, scoped JWT
      // (not Chariot platform token), and auth-gated loading. (R-002)

      script.onerror = error => {
        console.error('[Featurebase] Failed to load SDK:', error);
      };

      script.onload = async () => {
        if (!window.Featurebase) {
          console.error(
            '[Featurebase] SDK loaded but window.Featurebase not defined'
          );
          return;
        }

        // Fetch JWT from broker before booting Featurebase
        const jwtToken = await fetchFeaturebaseJwt(axios);

        try {
          // Boot Featurebase with user identification (required for security)
          window.Featurebase('boot', {
            appId: appId,
            email: userEmail, // User email for identification
            userId: userId, // User ID (using email as ID)
            featurebaseJwt: jwtToken, // JWT for Messenger auth (optional, undefined if fetch failed)
            theme: currentTheme, // Match Chariot's theme
            language: 'en', // English language
          });

          // Initialize changelog widget
          // 'praetorian' is our Featurebase org subdomain (praetorian.featurebase.app).
          // Different from appId which is the SDK app identifier used in boot.
          window.Featurebase('init_changelog_widget', {
            organization: 'praetorian',
            changelogCard: {
              enabled: true, // Non-intrusive card in corner showing latest update
            },
            popup: {
              enabled: true,
              usersName: displayName, // Personalized greeting in popup
              autoOpenForNewUpdates: true, // Auto-shows popup when new entries exist; first visit marks all as seen
            },
            theme: currentTheme,
            locale: 'en',
            featurebaseJwt: jwtToken, // Authenticate user directly - ensures popup tracking works even if boot fails
          });

          // Schedule token refresh if we have a JWT
          if (jwtToken) {
            scheduleTokenRefresh(jwtToken, axios);
          }
        } catch (error) {
          console.error('[Featurebase] Failed to initialize:', error);
        }
      };

      document.body.appendChild(script);
    }, 0);

    // Cleanup: DON'T remove script or reset flag to prevent re-injection
    // The widget should persist for the entire session
    return () => {
      // Clear deferred timer on unmount
      clearTimeout(deferredTimerId);

      // Clear refresh timer on unmount (will be rescheduled on remount if needed)
      if (refreshTimerId) {
        clearTimeout(refreshTimerId);
        refreshTimerId = null;
      }
      // Do NOT remove script or reset isFeaturebaseInjected flag
      // Widget should persist even if component remounts
    };

    // to prevent re-injection when user impersonates. Widget initializes once per session.
  }, [isSignedIn]);

  return null; // Non-rendering component
};
